import React, { useEffect, useState } from "react";
import Dropzone, { defaultClassNames, IDropzoneProps } from "react-dropzone-uploader";

import { Button, LoadingOverlay } from "@mantine/core";
import {
  ButtonWrapper,
  DropZoneStyle,
  DropZoneText,
  DropzoneWrapper,
  DropZoneWrapper,
  InnerWrapper,
  StyleButtonWrapper,
  StyleDescription,
  StyleDropzone,
  StyleDropzoneWrapper,
  StyleFooter,
  StyleValidationMessagePlaceHolder
} from "modules/imports/components/Import.styles";
import { ErrorResponse } from "types";
import Loader from "ui/feedback/Loader";
import { FileUploadIcon } from "ui/icons";
import ErrorModal from "ui/overlays/ErrorModal";
import Modal from "ui/overlays/Modal";
import { ModalTitle } from "ui/styles";

import { postImportSignedUrlService, putImportFileService } from "../api";
import { useUpdateAnalysisImportMutation } from "../hooks";
import { ImportInterface } from "../types";
import { ConfirmationUploadModal } from "./ConfirmationUploadModal";

type UploadAnalysisFileProps = {
  handleClose: () => void;
  showModal: boolean;
  transaction: ImportInterface | null;
};

export const UploadAnalysisFile: React.FC<UploadAnalysisFileProps> = ({ showModal, handleClose, transaction }) => {
  const { id = null, analysisFileLocation } = transaction || {};

  const [process, setProcess] = useState<boolean>(false);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [analysisFile, setAnalysisFile] = useState<File | undefined>();
  const [errorModal, setErrorModal] = useState<boolean>(false); // state controller for error Modal
  const [message, setMessage] = useState<string>(""); // message to display on success or error
  const updateAnalysisImportMutation = useUpdateAnalysisImportMutation();

  const Preview = (): JSX.Element => {
    return (
      <DropzoneWrapper key="Dropzone">
        <FileUploadIcon style={{ marginLeft: "auto", marginRight: "auto", marginBottom: "13px" }} />
        <DropZoneText>
          Drag and drop file(s) here or <span style={{ fontWeight: 700 }}>Browse Files</span>
          <div style={{ alignSelf: "flex-start", margin: "20px 3%", fontFamily: "Inter" }}>{analysisFile?.name}</div>
        </DropZoneText>
      </DropzoneWrapper>
    );
  };

  useEffect(() => {
    if (updateAnalysisImportMutation.isSuccess) {
      setAnalysisFile(undefined);
      setShowConfirmModal(true);
      setProcess(false);
    }
  }, [updateAnalysisImportMutation.isSuccess]);

  useEffect(() => {
    if (updateAnalysisImportMutation.isError === true) {
      setAnalysisFile(undefined);
      const errorMessage =
        (updateAnalysisImportMutation.error as ErrorResponse).response.data.errorMessage ?? "Something Went Wrong.";
      setMessage(errorMessage);
      setErrorModal(true);
      setProcess(false);
    }
  }, [updateAnalysisImportMutation.isError]);

  const handleUploadFile = async () => {
    if (analysisFile?.name === undefined || id === null || !analysisFileLocation) {
      return false;
    }
    setProcess(true);
    try {
      if (id) {
        const signedUrl = await postImportSignedUrlService(id, "analysis");
        await putImportFileService(signedUrl.data, analysisFile);
        await updateAnalysisImportMutation.mutateAsync({
          analysisFileLocation,
          id
        });
      }
    } catch (e) {
      setProcess(false);
      setMessage("Upload failed try again.");
      setErrorModal(true);
    }
  };

  const handleChangeStatus: IDropzoneProps["onChangeStatus"] = ({ file }, status) => {
    if (["getting_upload_params", "headers_received", "preparing", "uploading"].includes(status)) {
      setProcess(true);
    } else {
      setProcess(false);
    }
    if (status === "rejected_file_type") {
      setMessage("Accepted File Types: .csv only.");
      setErrorModal(true);
    }
    if (status === "exception_upload") {
      setAnalysisFile(undefined);
      setMessage("Upload failed try again.");
      setErrorModal(true);
    }
    if (status === "done") {
      setAnalysisFile(file);
    }
  };

  const handleResetDropzone = () => {
    setAnalysisFile(undefined);
    setProcess(false);
    handleClose();
  };

  const handleSubmit: IDropzoneProps["onSubmit"] = (_, allFiles) => {
    setAnalysisFile(undefined);
    allFiles.forEach(f => f.remove());
  };

  return (
    <React.Fragment>
      <Modal opened={showModal} handleClose={handleClose} withCloseButton>
        <ModalTitle>Add Analysis File</ModalTitle>
        <StyleDescription>
          Upload a new Analysis File. The file must follow the format for the corresponding Transactions File and
          include a "naicsCode" column with the classifications for each transaction.
        </StyleDescription>
        <LoadingOverlay
          visible={process || updateAnalysisImportMutation.isLoading}
          overlayBlur={2}
          loader={<Loader />}
        />
        <StyleDropzoneWrapper>
          <StyleDropzone>
            <DropZoneStyle>
              <DropZoneWrapper>
                <InnerWrapper>
                  <Dropzone
                    maxFiles={1}
                    multiple={false}
                    onChangeStatus={handleChangeStatus}
                    onSubmit={handleSubmit}
                    submitButtonContent={"Cancel"}
                    accept=".csv"
                    disabled={files =>
                      files.some(f => ["getting_upload_params", "preparing", "uploading"].includes(f.meta.status))
                    }
                    classNames={{ inputLabelWithFiles: defaultClassNames.inputLabel }}
                    inputContent={
                      <DropzoneWrapper key="Dropzone">
                        <FileUploadIcon style={{ marginLeft: "auto", marginRight: "auto", marginBottom: "13px" }} />
                        <DropZoneText>
                          Drag and drop file(s) here or <span style={{ fontWeight: 700 }}>Browse Files</span>
                        </DropZoneText>
                      </DropzoneWrapper>
                    }
                    PreviewComponent={Preview}
                  />
                </InnerWrapper>
              </DropZoneWrapper>
            </DropZoneStyle>
          </StyleDropzone>
          <StyleValidationMessagePlaceHolder>Accepted File Type: .csv only</StyleValidationMessagePlaceHolder>
        </StyleDropzoneWrapper>
        <StyleFooter>
          <ButtonWrapper>
            <Button
              disabled={process || updateAnalysisImportMutation.isLoading}
              variant="default"
              onClick={() => handleResetDropzone()}
            >
              Cancel
            </Button>
          </ButtonWrapper>
          <StyleButtonWrapper>
            <ButtonWrapper>
              <Button
                disabled={process || analysisFile?.name === undefined}
                onClick={() => {
                  handleUploadFile();
                }}
              >
                Upload
              </Button>
            </ButtonWrapper>
          </StyleButtonWrapper>
        </StyleFooter>
      </Modal>
      <ConfirmationUploadModal
        openModal={showConfirmModal}
        setOpenModal={setShowConfirmModal}
        handleClose={handleClose}
        title="Your Analysis File has been submitted!"
        description="You may check the status of this import on the Imports page."
        addMoreBtnText="Add More Data Files"
        type="analysis"
      />
      <ErrorModal
        openModal={errorModal}
        setOpenModal={setErrorModal}
        message={message}
        onClose={() => setErrorModal(false)}
      />
    </React.Fragment>
  );
};
